import React, { useContext, useState } from 'react';
import axios from 'axios';
import { AuthContext } from '../App';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import { Send } from 'lucide-react';

/**
 * SendDirectMessageDialog — a small controlled dialog admins and prayer
 * moderators use to send a one-off note to a specific member.
 *
 * Props:
 *   open, onOpenChange     — controlled dialog state
 *   recipient              — { id, name } — required
 *   prayerId               — optional context; prayer moderators must supply
 *                            this when the recipient isn't already known
 *                            to have submitted prayers.
 *   defaultSubject         — optional pre-fill for the subject line
 *   onSent                 — optional callback after a successful send
 */
const SendDirectMessageDialog = ({
  open,
  onOpenChange,
  recipient,
  prayerId,
  defaultSubject = '',
  onSent,
}) => {
  const { API } = useContext(AuthContext);
  const [subject, setSubject] = useState(defaultSubject);
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);

  // Reset when the dialog opens for a new recipient / prayer.
  React.useEffect(() => {
    if (open) {
      setSubject(defaultSubject);
      setMessage('');
    }
  }, [open, defaultSubject]);

  const handleSend = async () => {
    const text = message.trim();
    if (!text) {
      toast.error('Please write a message');
      return;
    }
    if (!recipient?.id) {
      toast.error('No recipient selected');
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/admin/messages/send`, {
        recipient_id: recipient.id,
        subject: subject.trim(),
        message: text,
        prayer_id: prayerId || null,
      });
      toast.success(`Message sent to ${recipient.name || 'member'}`);
      onOpenChange(false);
      if (typeof onSent === 'function') onSent();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send message');
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="send-dm-dialog">
        <DialogHeader>
          <DialogTitle>Message {recipient?.name || 'member'}</DialogTitle>
          <DialogDescription>
            {prayerId
              ? "They'll get a notification linking back to this conversation."
              : 'A private note delivered to their in-app inbox.'}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <Input
            placeholder="Subject (optional)"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            maxLength={200}
            data-testid="send-dm-subject"
          />
          <Textarea
            placeholder="Write your message…"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            rows={6}
            maxLength={4000}
            data-testid="send-dm-message"
          />
          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={sending}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSend}
              disabled={sending || !message.trim()}
              className="bg-[#2B6B7F]"
              data-testid="send-dm-submit"
            >
              <Send className="w-4 h-4 mr-1" />
              {sending ? 'Sending…' : 'Send'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SendDirectMessageDialog;
